import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Toast from "./admin/Toast";

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  const redirectTo = location.state?.from?.pathname || "/admin/dashboard";

  const hideToast = useCallback(() => {
    setToast({ show: false, message: "", type: "info" });
  }, []);

  const addToast = useCallback((type, message) => {
    setToast({ show: true, message, type });
  }, []);

  useEffect(() => {
    if (location.state?.loggedOut) {
      addToast("success", t("login.toastLoggedOut"));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, addToast, t]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, isAuthenticated, navigate, redirectTo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (error) setError("");
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await login(formData);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || t("login.error"));
      setIsSubmitting(false);
    }
  };

  /** Größere Login-Felder; optisch zentrierter Text (line-height) */
  const inputClass = (hasError) =>
    `w-full h-12 pl-12 pr-4 border rounded-xl text-base text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 leading-normal focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
      hasError
        ? "bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-700 focus:ring-red-500/20 focus:border-red-400"
        : "border-slate-200 dark:border-slate-600"
    }`;

  const passwordInputClass = (hasError) =>
    `w-full h-12 pl-12 pr-14 border rounded-xl text-base text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 leading-normal focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
      hasError
        ? "bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-700 focus:ring-red-500/20 focus:border-red-400"
        : "border-slate-200 dark:border-slate-600"
    }`;

  return (
    <main className="min-h-screen min-h-[100dvh] flex flex-col bg-slate-50/50 dark:bg-slate-900 transition-colors">
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8 w-full min-h-0">
        <div className="w-full max-w-md flex flex-col items-stretch">
          <div className="mb-5 self-start w-full">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 dark:focus-visible:ring-primary-light/20 rounded-lg px-1 py-1 -ml-1"
            >
              <i className="fas fa-arrow-left text-[10px] opacity-80" aria-hidden />
              {t("login.backToWebsite")}
            </button>
          </div>

          {/* Kartenaufbau wie ParticipantFormTailwind: Kopfzeile mit border-b */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60 flex items-start gap-3">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center"
                aria-hidden
              >
                <i className="fas fa-shield-alt text-primary dark:text-primary-light text-sm" />
              </div>
              <div className="min-w-0 pt-0.5">
                <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("login.title")}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-snug">
                  {t("login.subtitle")}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4" noValidate>
              <div>
                <label
                  htmlFor="username"
                  className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5"
                >
                  {t("login.username")}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    aria-invalid={error ? "true" : "false"}
                    aria-describedby={error ? "login-error" : undefined}
                    className={inputClass(!!error)}
                    placeholder={t("login.usernamePlaceholder")}
                    required
                  />
                  <span
                    className="pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center justify-center text-slate-400 dark:text-slate-500"
                    aria-hidden
                  >
                    <i className="fas fa-user text-sm leading-none" />
                  </span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5"
                >
                  {t("login.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    aria-invalid={error ? "true" : "false"}
                    aria-describedby={error ? "login-error" : undefined}
                    className={passwordInputClass(!!error)}
                    placeholder={t("login.password")}
                    required
                  />
                  <span
                    className="pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center justify-center text-slate-400 dark:text-slate-500"
                    aria-hidden
                  >
                    <i className="fas fa-lock text-sm leading-none" />
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute top-1/2 right-2 -translate-y-1/2 h-10 w-10 shrink-0 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200/80 dark:hover:bg-slate-600/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                    aria-pressed={showPassword}
                    aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  >
                    <i className={`fas text-sm leading-none ${showPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden />
                  </button>
                </div>
              </div>

              <div
                id="login-error"
                role="alert"
                aria-live="assertive"
                className={`overflow-hidden transition-all duration-200 ${error ? "max-h-28 opacity-100" : "max-h-0 opacity-0"}`}
              >
                {error ? (
                  <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
                    <div className="flex gap-2">
                      <i className="fas fa-exclamation-circle text-xs mt-0.5 flex-shrink-0" aria-hidden />
                      <span>{error}</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="px-3 py-2.5 bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl">
                <div className="flex gap-2.5">
                  <div
                    className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white dark:bg-slate-800 text-primary dark:text-primary-light border border-slate-200 dark:border-slate-600"
                    aria-hidden
                  >
                    <i className="fas fa-info-circle text-[9px]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">
                      {t("login.helpTitle")}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      {t("login.helpText")}
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full inline-flex h-12 items-center justify-center gap-2 px-4 bg-primary dark:bg-primary-light text-white text-base font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i
                  className={`fas text-sm leading-none ${isSubmitting ? "fa-spinner fa-spin" : "fa-sign-in-alt"}`}
                  aria-hidden
                />
                {isSubmitting ? t("login.submitting") : t("login.submit")}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6 sm:mt-8">
            {t("login.footerCopyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </main>
  );
}

export default Login;
